import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetDeliveryPointById } from '../../hooks/admin-get-delivery-point-by-id.hook';
import { useUpdateDeliveryPoint } from '../../hooks/update-delivery-point.hook';
import { useArchiveDeliveryPoint } from '../../hooks/archive-delivery-point.hook';
import { useRestoreDeliveryPoint } from '../../hooks/restore-delivery-point.hook';
import { UpdateDeliveryPointRequestDTO } from '../../types/dtos/update-delivery-point.dto';
import { ADMIN_DELIVERY_POINT_APP_PATHS } from '../../constants/delivery-points-app-paths.constants';
import {
  DELIVERY_POINT_DETAILS_CONFIRM_DIALOG_CONTENT,
  DELIVERY_POINT_DETAILS_TABS,
  resolveDeliveryPointDetailsTab,
} from '../../constants/delivery-point-details.constants';
import DeliveryPointInformationsTab from '../../components/Containers/DeliveryPointInformationsTab.component';
import DeliveryPointOrdersTab from '../../components/Containers/DeliveryPointOrdersTab.component';
import { DeliveryPointDetailsHeader } from '../../components/Containers/DeliveryPointDetailsHeader.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { NotFoundState } from '../../../shared/components/States/NotFoundState.component';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: 'archive' | 'restore' };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },
});

export default function AdminDeliveryPointDetailsView() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { deliveryPointId } = useParams<{ deliveryPointId: string }>();

  const currentTab = resolveDeliveryPointDetailsTab(new URLSearchParams(location.search).get('tab'));

  const { isLoading, deliveryPoint, refetch } = useAdminGetDeliveryPointById({ deliveryPointId });
  const { updateDeliveryPoint, isSubmitting: isUpdating } = useUpdateDeliveryPoint();
  const { archiveDeliveryPoint, isSubmitting: isArchiving } = useArchiveDeliveryPoint();
  const { restoreDeliveryPoint, isSubmitting: isRestoring } = useRestoreDeliveryPoint();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleDeliveryPointUpdated = async (editedFields: UpdateDeliveryPointRequestDTO): Promise<boolean> => {
    if (isLoading || !deliveryPoint) return false;

    const updatedDeliveryPoint = await updateDeliveryPoint({
      deliveryPointId: deliveryPoint.id,
      deliveryPointData: editedFields,
    });
    if (!updatedDeliveryPoint) return false;

    await refetch();
    return true;
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !deliveryPoint) return;

    const result =
      dialog.action === 'archive'
        ? await archiveDeliveryPoint(deliveryPoint.id)
        : await restoreDeliveryPoint(deliveryPoint.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && !deliveryPoint && (
        <NotFoundState
          title="Point de livraison introuvable"
          backLabel="Retour aux points de livraison"
          backTo={ADMIN_DELIVERY_POINT_APP_PATHS.DELIVERY_POINTS_LIST}
        />
      )}
      {!isLoading && deliveryPoint && (
        <React.Fragment>
          <DeliveryPointDetailsHeader
            deliveryPoint={deliveryPoint}
            onArchiveActionClick={() =>
              setDialog({ kind: 'confirm', action: deliveryPoint.archivedAt ? 'restore' : 'archive' })
            }
          />
          <TabsController
            size="small"
            tabs={DELIVERY_POINT_DETAILS_TABS}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'informations' && (
            <DeliveryPointInformationsTab
              deliveryPoint={deliveryPoint}
              isUpdating={isUpdating}
              updateDeliveryPoint={handleDeliveryPointUpdated}
            />
          )}
          {currentTab === 'orders' && <DeliveryPointOrdersTab deliveryPoint={deliveryPoint} />}
        </React.Fragment>
      )}
      {dialog.kind === 'confirm' && deliveryPoint && (
        <ConfirmActionDialog
          open
          title={DELIVERY_POINT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={DELIVERY_POINT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].describe(deliveryPoint.name)}
          confirmLabel={DELIVERY_POINT_DETAILS_CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
